import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  Grid,
  GridColumn,
  GridCustomCellProps,
  GridCustomRowProps,
  GridCustomHeaderCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid';
import { PagerProps } from '@progress/kendo-react-data-tools';
import { IconButton, Button, Tooltip, Box, CircularProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { Columns, DateFiltersByField, TemplateFilterInput } from '@/types';
import { getEquipmentColumnsFromAssemblyTemplate } from '@/utils';
import { getPoiIconsColumn } from '@/utils/getPoiColumns';
import { useHavePermission, useRouter } from '@/hooks';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  Assembly,
  TemplateFieldTypeEnum,
  useGetAssembliesByTemplateFieldsFiltersForFilterSideBarLazyQuery,
  useGetUserPreferencesQuery,
} from '@/__generated__/graphql';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { ColumnsSelectionDialog } from '../ColumnsSelectionDialog';
import { ErrorLogger } from '@/utils/error-logger';
import {
  INSIGHTS_GET_FILTERED_ASSEMBLIES_ERROR,
  INSIGHTS_USER_PREFERENCE_ERROR,
} from '@/constants/Errors';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import useLiveSyncAllPois from '../../shared/hooks/useLiveSyncAllPois';
import { useKendoInlineEdit } from '@/hooks/useKendoInlineEdit';
import { EditCellModal } from '../../shared/EquipmentTable/EditCellModal';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { KendoPager } from '@/components/shared/KendoGrid';
import { PageLoader } from '@/components/shared/PageLoader/PageLoader';
import { getColumnDimensions } from './utils';
import { CustomHeaderCell } from './CustomHeaderCell';
import { CustomCell } from './CustomCell';
import './KendoEquipmentTable.css';
import { SaveViewModal } from './SaveViewModal';
import InspectionPlansPills from './InspectionPlansPills';
import { ColumnModal } from './ColumnModal';
import { LoadViewModal } from '../LoadViewModal/LoadViewModal';
import { Permissions } from '@/types';
import { RowRender } from './RowRender';
import KendoAIChatAssitant from './KendoAIChatAssistant';
import AIChat from './AIChat';
import {
  ComponentReportsProvider,
  REPORTS_COLUMN_KEY,
  REPORTS_COLUMN_NAME,
} from '@/components/shared/ComponentReportsTable';
type Props = {
  saveSingleEdit?: (itemId: string, columnKey: string, value: unknown) => Promise<void>;
  saveBulkEdit?: (columnKey: string, value: unknown, itemIds: string[]) => Promise<void>;
};

const MyPager = (props: PagerProps) => {
  return (
    <div className="kendo-equipment-table-footer">
      <KendoPager
        skip={props.skip}
        take={props.take}
        total={props.total}
        onPageChange={props.onPageChange}
        pageSizes={[10, 20, 50, 100]}
        buttonCount={5}
        info={true}
        previousNext={true}
        responsive={true}
      />
    </div>
  );
};

const getView3DColumn = (
  router: ReturnType<typeof useRouter>,
  structureId: string,
  setSelectedAssemblyId: (id: string | undefined) => void
): Columns => ({
  isSortable: false,
  visibility: true,
  key: 'view3D',
  columnName: 'View 3D',
  displayConfig: {
    type: '3DBUTTON',
  },
  onCellClick: (event, item) => {
    event.stopPropagation();
    setSelectedAssemblyId(undefined);
    const url = `/analysis/${structureId}/viewer?assembly=${item?.tagName}`;
    const isCtrlClick = event.ctrlKey || event.metaKey;
    if (isCtrlClick) window.open(url, '_blank');
    else router.push(url);
  },
});

export const KendoEquipmentTable = ({ saveSingleEdit, saveBulkEdit }: Props) => {
  const [page, setPage] = useState(0);
  const [allColumns, setAllColumns] = useState<Columns[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [tooltipContainer, setTooltipContainer] = useState<HTMLDivElement | null>(null);
  const setInsightsExplorerLoading = useSetAtom(state.insightsExplorerLoadingState);
  const [allAssemblies, setAllAssemblies] = useAtom(state.allAssembliesForInsights);
  const [assembliesCount, setAssembliesCount] = useAtom(state.assembliesCount);
  const [, setLoadingTable] = useState(true);
  const setAssembliesLoading = useSetAtom(state.assembliesLoading);
  const assembliesLoading = useAtomValue(state.assembliesLoading);
  const [selectedAssemblyIds, setSelectedAssemblyIds] = useAtom(state.selectedAssemblyIds);
  const [dialogOpen, setDialogOpen] = useState(false);
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const templateId = useSelectedAssemblyTemplateId()!;
  const [selectedTemplateFilters, setSelectedTemplateFilters] = useAtom(
    state.selectedTemplateFiltersState
  );
  const appliedDateFiltersValue = useAtomValue(state.appliedDateFilters);
  const filteredDeckIds = useAtomValue(state.selectedFilteredDeckIds);
  const selectedPaintRegionIds = useAtomValue(state.selectedFilteredPaintRegionIds);
  const selectedWorkpackIds = useAtomValue(state.selectedFilteredWorkpackIds);
  const selectedPoiTypes = useAtomValue(state.selectedFilteredPoiTypes);
  const insightsSearchTerm = useAtomValue(state.insightsSearchBarSearchTerm);
  const insightsRangeSearchTerm = useAtomValue(state.insightsSearchBarRangeSearchTerm);
  const setAppliedAssemblyFilters = useSetAtom(state.appliedAssemblyFilters);
  const [appliedTemplateFilters, setAppliedTemplateFilters] = useState(selectedTemplateFilters);
  const [appliedDeckFilters, setAppliedDeckFilters] = useState(filteredDeckIds);
  const [appliedPaintRegionFilters, setAppliedPaintRegionFilters] =
    useState(selectedPaintRegionIds);
  const [appliedWorkpackFilters, setAppliedWorkpackFilters] = useState(selectedWorkpackIds);
  const [appliedPoiTypeFilters, setAppliedPoiTypeFilters] = useState(selectedPoiTypes);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(insightsSearchTerm);
  const [appliedRangeSearchTerm, setAppliedRangeSearchTerm] = useState(insightsRangeSearchTerm);

  useEffect(() => {
    setAppliedSearchTerm(insightsSearchTerm);
  }, [insightsSearchTerm]);
  useEffect(() => {
    setAppliedRangeSearchTerm(insightsRangeSearchTerm);
  }, [insightsRangeSearchTerm]);
  const setAllComponentsSelected = useSetAtom(state.isAllComponentsSelected);
  const { showSnackBar } = useSnackBarMessage({});
  const [headerEditOpen, setHeaderEditOpen] = useState(false);
  const [headerEditColumnKey, setHeaderEditColumnKey] = useState<string>('');
  const [headerEditColumnName, setHeaderEditColumnName] = useState<string>('');
  const [headerEditColumnMeta, setHeaderEditColumnMeta] = useState<Columns | null>(null);
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [columnToEdit, setColumnToEdit] = useState<Columns | null>(null);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [loadViewOpen, setLoadViewOpen] = useState(false);

  const selectedTemplateFiltersWithDates = useMemo(() => {
    const dateFiltersAsTemplateFilters: TemplateFilterInput[] = Object.entries(
      appliedDateFiltersValue
    ).flatMap(([fieldKey, filter]) => {
      if (filter.type === 'single' && filter.date) {
        return [{ id: fieldKey, value: [], startDate: filter.date, endDate: filter.date }];
      }
      if (filter.type === 'range' && filter.startDate && filter.endDate) {
        return [
          {
            id: fieldKey,
            value: [],
            startDate: filter.startDate,
            endDate: filter.endDate,
          },
        ];
      }
      return [];
    });

    return [...selectedTemplateFilters, ...dateFiltersAsTemplateFilters];
  }, [appliedDateFiltersValue, selectedTemplateFilters]);
  const appliedFilters = useAtomValue(state.filters);

  useEffect(() => {
    setAppliedPaintRegionFilters(selectedPaintRegionIds);
    setAppliedAssemblyFilters((prev) => ({
      ...prev,
      paintRegionFilters: selectedPaintRegionIds,
    }));
  }, [selectedPaintRegionIds, setAppliedAssemblyFilters]);

  const [applyFilters, setApplyFilters] = useAtom(state.applyFilters);
  const [refetchEquipmentTable] = useAtom(state.refetchEquipmentTable);
  const structureId = useGetStructureId();
  const [sortDirection, setSortDirection] = useState<number>(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const {
    data: userPreferenceData,
    error: userPreferenceError,
    loading: userPreferenceLoading,
  } = useGetUserPreferencesQuery({
    fetchPolicy: 'cache-first',
  });
  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const setSelectedAssemblyName = useSetAtom(state.selectedAssemblyName);
  const { getUnitSuffix } = useUnitConversion();
  const { edit, setEdit, handleEditChange, exitEdit } = useKendoInlineEdit();

  useLiveSyncAllPois(() => {
    if (structureId && templateId) {
      fetchFilteredAssemblies({
        variables: {
          input: {
            selectedTemplateFilters: appliedTemplateFilters,
            structureId,
            templateId,
            selectedDeckIds: appliedDeckFilters,
            selectedPaintRegionIds: appliedPaintRegionFilters,
            selectedWorkpackIds: appliedWorkpackFilters,
            ...(appliedPoiTypeFilters.length > 0 && {
              selectedPoiTypes: appliedPoiTypeFilters,
            }),
            ...(appliedSearchTerm && {
              searchTerms: appliedSearchTerm.split('||').filter((t) => t.trim()),
            }),
            ...(appliedRangeSearchTerm && {
              rangeSearchTerm: appliedRangeSearchTerm,
              unitSystem: userPreferenceData?.getUserPreferences?.unit,
            }),
          },
          pageInfo: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            sortBy,
            sortDir: sortDirection,
          },
        },
      });
    }
  });

  const [fetchFilteredAssemblies] =
    useGetAssembliesByTemplateFieldsFiltersForFilterSideBarLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setAllAssemblies(data?.getAssembliesByTemplateFieldsFilters ?? []);
        setAssembliesCount(data?.getAssembliesCount?.count ?? 0);
        setApplyFilters(false);
        setInsightsExplorerLoading(false);
        setLoadingTable(false);
        setAssembliesLoading(false);
      },
      onError: (err: Error) => {
        ErrorLogger.log(
          INSIGHTS_GET_FILTERED_ASSEMBLIES_ERROR.withContext({
            input: {
              selectedTemplateFilters: appliedTemplateFilters,
              structureId,
              templateId,
              selectedDeckIds: appliedDeckFilters,
              selectedPaintRegionIds: appliedPaintRegionFilters,
              selectedWorkpackIds: appliedWorkpackFilters,
              ...(appliedPoiTypeFilters.length > 0 && {
                selectedPoiTypes: appliedPoiTypeFilters,
              }),
              searchTerm: appliedSearchTerm,
              ...(appliedRangeSearchTerm && {
                rangeSearchTerm: appliedRangeSearchTerm,
                unitSystem: userPreferenceData?.getUserPreferences?.unit,
              }),
            },
            pageInfo: {
              skip: page * rowsPerPage,
              limit: rowsPerPage,
              sortBy,
              sortDir: sortDirection,
            },
          }).withError(err)
        );
        setApplyFilters(false);
        setInsightsExplorerLoading(false);
        setLoadingTable(false);
        setAssembliesLoading(false);
      },
    });

  const getFilteredAssemblies = useCallback(() => {
    if (structureId && templateId) {
      setAssembliesLoading(true);
      fetchFilteredAssemblies({
        variables: {
          input: {
            selectedTemplateFilters: appliedTemplateFilters,
            structureId,
            templateId,
            selectedDeckIds: appliedDeckFilters,
            selectedPaintRegionIds: appliedPaintRegionFilters,
            selectedWorkpackIds: appliedWorkpackFilters,
            ...(appliedPoiTypeFilters.length > 0 && {
              selectedPoiTypes: appliedPoiTypeFilters,
            }),
            ...(appliedSearchTerm && {
              searchTerms: appliedSearchTerm.split('||').filter((t) => t.trim()),
            }),
            ...(appliedRangeSearchTerm && {
              rangeSearchTerm: appliedRangeSearchTerm,
              unitSystem: userPreferenceData?.getUserPreferences?.unit,
            }),
          },
          pageInfo: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            sortBy,
            sortDir: sortDirection,
          },
        },
      });
    }
  }, [
    appliedDeckFilters,
    appliedPaintRegionFilters,
    appliedTemplateFilters,
    appliedWorkpackFilters,
    appliedPoiTypeFilters,
    appliedSearchTerm,
    appliedRangeSearchTerm,
    fetchFilteredAssemblies,
    page,
    rowsPerPage,
    setAssembliesLoading,
    sortBy,
    sortDirection,
    structureId,
    templateId,
    userPreferenceData?.getUserPreferences?.unit,
  ]);
  // Refetch Equipment Table
  useEffect(() => {
    if (templateId) getFilteredAssemblies();
  }, [refetchEquipmentTable, getFilteredAssemblies, templateId]);

  useEffect(() => {
    if (templateId && applyFilters) {
      getFilteredAssemblies();
    }
  }, [applyFilters, getFilteredAssemblies, templateId]);

  useEffect(() => {
    getFilteredAssemblies();
  }, [getFilteredAssemblies, rowsPerPage, page]);

  useEffect(() => {
    if (applyFilters) {
      setAppliedTemplateFilters(selectedTemplateFiltersWithDates);
      setAppliedDeckFilters(filteredDeckIds);
      setAppliedPaintRegionFilters(selectedPaintRegionIds);
      setAppliedWorkpackFilters(selectedWorkpackIds);
      setAppliedPoiTypeFilters(selectedPoiTypes);
      setAppliedSearchTerm(insightsSearchTerm);
      setPage(0);
      setAppliedAssemblyFilters((prev) => ({
        ...prev,
        templateFilters: selectedTemplateFiltersWithDates,
        deckFilters: filteredDeckIds,
        paintRegionFilters: selectedPaintRegionIds,
        workpackFilters: selectedWorkpackIds,
        poiTypeFilters: selectedPoiTypes,
      }));
    }
  }, [
    templateId,
    selectedTemplateFilters,
    selectedTemplateFiltersWithDates,
    filteredDeckIds,
    selectedPaintRegionIds,
    selectedWorkpackIds,
    selectedPoiTypes,
    insightsSearchTerm,
    applyFilters,
    fetchFilteredAssemblies,
    structureId,
    page,
    rowsPerPage,
    setAppliedAssemblyFilters,
  ]);

  const router = useRouter();
  const isAdmin = useFeatureFlag('admin');
  const canManageInspectionPlans = useHavePermission(Permissions.MANAGE_INSPECTION_PLANS);

  useEffect(() => {
    const assemblyFromUrl = router.query?.assembly;
    if (assemblyFromUrl && allAssemblies?.length) {
      const found = allAssemblies.find((a) => a.tagName === assemblyFromUrl);
      if (found) {
        setSelectedAssemblyId(found.id);
        setSelectedAssemblyName(found.tagName);
      }
    }
  }, [router.query?.assembly, allAssemblies, setSelectedAssemblyId, setSelectedAssemblyName]);

  const dateFieldKeys = useMemo(() => {
    if (!assemblyTemplate?.fields) return [];
    return assemblyTemplate.fields
      .filter((f) => f.type === TemplateFieldTypeEnum.Date && f.key)
      .map((f) => f.key as string);
  }, [assemblyTemplate]);

  const parseDateOnly = (raw: string) => {
    const datePart = raw.slice(0, 10);
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return Number.NaN;
    return new Date(y, m - 1, d).getTime();
  };

  const displayedAssemblies = useMemo(() => {
    if (!allAssemblies) return allAssemblies;
    const dateFilters: DateFiltersByField | undefined = appliedFilters?.dateFilter;
    if (!dateFilters || dateFieldKeys.length === 0) return allAssemblies;
    const activeDateFilters = Object.entries(dateFilters).filter(([fieldKey]) =>
      dateFieldKeys.includes(fieldKey)
    );
    if (!activeDateFilters.length) return allAssemblies;

    return allAssemblies.filter((assembly) => {
      const fieldData = assembly.templateFieldData as Record<string, string> | undefined;
      if (!fieldData) return false;

      return activeDateFilters.every(([fieldKey, dateFilter]) => {
        const rawValue = fieldData[fieldKey];
        if (!rawValue) return false;
        const fieldDate = parseDateOnly(rawValue);
        if (Number.isNaN(fieldDate)) return false;

        if (dateFilter.type === 'single') {
          const target = parseDateOnly(dateFilter.date);
          return fieldDate === target;
        }
        const start = parseDateOnly(dateFilter.startDate);
        const end = parseDateOnly(dateFilter.endDate);
        return fieldDate >= start && fieldDate <= end;
      });
    });
  }, [allAssemblies, appliedFilters?.dateFilter, dateFieldKeys]);

  const initialColumns = useMemo(() => {
    if (!assemblyTemplate) return [];
    const allTemplateColumns = getEquipmentColumnsFromAssemblyTemplate(assemblyTemplate);
    const { plannerColumns, columnsFromTemplate } = allTemplateColumns.reduce<{
      plannerColumns: Columns[];
      columnsFromTemplate: Columns[];
    }>(
      (acc, column) => {
        if (column.isPlannerColumn) {
          acc.plannerColumns.push(column);
        } else {
          acc.columnsFromTemplate.push(column);
        }
        return acc;
      },
      { plannerColumns: [], columnsFromTemplate: [] }
    );
    const poiIconsColumn = getPoiIconsColumn();
    const view3dColumn = [getView3DColumn(router, structureId, setSelectedAssemblyId)];
    const reportsColumn: Columns = {
      isSortable: false,
      visibility: true,
      key: REPORTS_COLUMN_KEY,
      columnName: REPORTS_COLUMN_NAME,
      displayConfig: { type: 'REPORTS' },
    };
    return [
      {
        isSortable: true,
        visibility: true,
        key: 'tagName',
        columnName: 'Component',
        locked: true,
      },
      ...columnsFromTemplate,
      poiIconsColumn,
      reportsColumn,
      ...plannerColumns,
      ...view3dColumn,
    ] as Columns[];
  }, [assemblyTemplate, router, structureId, setSelectedAssemblyId]);

  useEffect(() => {
    setAllColumns((prevColumns) => {
      if (visibleColumns.length > 0) {
        return initialColumns.map((column) => ({
          ...column,
          visibility: visibleColumns.includes(column.columnName),
        }));
      }
      if (prevColumns.length === 0 || prevColumns.length !== initialColumns.length) {
        const defaultColumns = new Set(
          initialColumns.filter(({ visibility }) => visibility).map(({ columnName }) => columnName)
        );
        return initialColumns.map((column) => ({
          ...column,
          visibility: defaultColumns.has(column.columnName),
        }));
      }
      return initialColumns.map((column) => {
        const existingColumn = prevColumns.find((c) => c.columnName === column.columnName);
        return {
          ...column,
          visibility: existingColumn?.visibility ?? column.visibility,
        };
      });
    });
  }, [initialColumns, visibleColumns]);

  useEffect(() => {
    if (userPreferenceLoading) {
      return;
    }
    if (userPreferenceError) {
      ErrorLogger.log(
        INSIGHTS_USER_PREFERENCE_ERROR.withContext({ structureId }).withError(userPreferenceError)
      );
    }
    const preferencesList = userPreferenceData?.getUserPreferences?.structure?.[structureId];
    const userColumns = preferencesList?.columns?.COMPONENT ?? [];
    let nextVisible =
      userColumns.length > 0
        ? [...userColumns]
        : initialColumns
            .filter((c) => c.visibility)
            .slice(0, 8)
            .map((c) => c.columnName);
    if (!nextVisible.includes('Component')) {
      nextVisible = ['Component', ...nextVisible];
    }
    setVisibleColumns(nextVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    structureId,
    userPreferenceData?.getUserPreferences?.structure,
    userPreferenceError,
    userPreferenceLoading,
  ]);

  const toggleDialog = useCallback(() => {
    setDialogOpen(!dialogOpen);
  }, [dialogOpen]);

  const handleSort = useCallback(
    (property: string) => {
      const isAsc = sortBy === property && sortDirection === 1;
      setSortDirection(isAsc ? -1 : 1);
      setSortBy(property);
    },
    [sortDirection, sortBy]
  );

  const activeColumns = useMemo(() => {
    return allColumns
      .filter(({ visibility }) => visibility)
      .map((column) => {
        const { displayUnit, unit, columnName, templateFieldKey } = column;
        const unitSuffix = getUnitSuffix({
          unit: displayUnit ?? unit,
          field: templateFieldKey,
        });
        let upperCaseColumnName = columnName.toUpperCase();
        upperCaseColumnName = unitSuffix
          ? `${upperCaseColumnName} (${unitSuffix})`
          : upperCaseColumnName;
        return { ...column, columnName: upperCaseColumnName };
      });
  }, [allColumns, getUnitSuffix]);

  const dialogColumns = useMemo(() => {
    return allColumns.map(
      ({
        columnName,
        visibility,
        displayUnit,
        unit,
        templateFieldKey,
        enableInlineEdit,
        isPlannerColumn,
      }) => {
        return {
          name: columnName,
          key: columnName,
          isSelected: visibility,
          displayUnit: displayUnit ?? '',
          unit: unit ?? '',
          templateFieldKey,
          enableInlineEdit,
          isPlannerColumn,
        };
      }
    );
  }, [allColumns]);

  const handleApplyColumns = useCallback((selectedColumnsKeys: string[]) => {
    setVisibleColumns(selectedColumnsKeys);
    const selectedColumnsSet = new Set(selectedColumnsKeys);
    setAllColumns((previous) =>
      previous.map((column) => ({
        ...column,
        visibility: selectedColumnsSet.has(column.columnName ?? ''),
      }))
    );
  }, []);

  const handlePageChange = useCallback(
    (event: GridPageChangeEvent) => {
      const newSkip = event.page?.skip ?? 0;
      const newTake = event.page?.take ?? rowsPerPage;
      const newPage = Math.floor(newSkip / newTake);
      setPage(newPage);
      if (newTake !== rowsPerPage) {
        setRowsPerPage(newTake);
      }
    },
    [rowsPerPage]
  );

  const handleAISort = useCallback(
    (field: string, direction: 'asc' | 'desc') => {
      const normalized = field.trim().toLowerCase();
      const isBoundary = ['component', 'tagname', 'tag_name'].includes(normalized);
      const sortField = isBoundary
        ? 'tagName'
        : allColumns.find(
            (col) =>
              col.columnName.toLowerCase() === normalized || col.key.toLowerCase() === normalized
          )?.key || field;
      setSortBy(sortField);
      setSortDirection(direction === 'asc' ? 1 : -1);
      setPage(0);
    },
    [allColumns]
  );

  const handleAIShowAllColumns = useCallback(() => {
    const names = allColumns.map((col) => col.columnName);
    setVisibleColumns(names);
    setAllColumns((prev) => prev.map((col) => ({ ...col, visibility: true })));
    showSnackBar('All columns are now visible', 'success');
  }, [allColumns, showSnackBar]);

  const handleAIHideColumn = useCallback(
    (columnName: string) => {
      setVisibleColumns((prev) => prev.filter((name) => name !== columnName));
      setAllColumns((prev) =>
        prev.map((col) => (col.columnName === columnName ? { ...col, visibility: false } : col))
      );
      showSnackBar(`Hidden ${columnName} column`, 'success');
    },
    [showSnackBar]
  );

  const handleAIReset = useCallback(() => {
    setSortBy('createdAt');
    setSortDirection(1);
    setPage(0);
    setSelectedTemplateFilters([]);
    getFilteredAssemblies();
    showSnackBar('Reset all filters and sorting', 'success');
  }, [getFilteredAssemblies, setSelectedTemplateFilters, showSnackBar]);

  const handleAIHideAllColumns = useCallback(() => {
    setVisibleColumns(['Component']);
    setAllColumns((prev) =>
      prev.map((col) => ({ ...col, visibility: col.columnName === 'Component' }))
    );
    showSnackBar('All columns are now hidden (except Component)', 'success');
  }, [showSnackBar]);

  const handleAIUpdateAssemblies = useCallback(
    (assemblies: Assembly[]) => {
      if (assemblies.length > 0) {
        setAllAssemblies(assemblies);
        setAssembliesCount(assemblies.length);
        showSnackBar(`Updated table with ${assemblies.length} components from AI`, 'success');
      }
      setPage(0);
    },
    [setAllAssemblies, setAssembliesCount, showSnackBar]
  );

  const handleAIAppliedFiltersChange = useCallback(
    (filters: Array<{ id: string; value: string[] }>) => {
      // The selectedTemplateFilters atom expects an array of TemplateFilterInput.
      // Replace the entire value with the incoming filters array from the AI assistant.
      setSelectedTemplateFilters(filters);
    },
    [setSelectedTemplateFilters]
  );

  const handleRowCheckBoxChange = useCallback(
    (assemblyId: string) => {
      setSelectedAssemblyIds((prev) => {
        if (prev.includes(assemblyId)) {
          return prev.filter((id) => id !== assemblyId);
        }
        return [...prev, assemblyId];
      });
    },
    [setSelectedAssemblyIds]
  );

  const headerCheckBoxChange = useCallback(() => {
    if (!displayedAssemblies) return;
    const allIds = displayedAssemblies.map((a) => a.id);
    if (selectedAssemblyIds.length === allIds.length) {
      setSelectedAssemblyIds([]);
      setAllComponentsSelected(false);
    } else {
      setSelectedAssemblyIds(allIds);
      setAllComponentsSelected(true);
    }
  }, [
    displayedAssemblies,
    selectedAssemblyIds.length,
    setSelectedAssemblyIds,
    setAllComponentsSelected,
  ]);

  const handleRowClick = useCallback(
    (dataItem: Assembly) => {
      setSelectedAssemblyId(dataItem.id);
      setSelectedAssemblyName(dataItem.tagName);
    },
    [setSelectedAssemblyId, setSelectedAssemblyName]
  );

  const onOpenHeaderEditModal = useCallback(
    (columnKey: string, columnName: string) => {
      const columnMeta = allColumns.find((c) => String(c.key) === String(columnKey)) ?? null;
      setHeaderEditColumnKey(columnKey);
      setHeaderEditColumnName(columnName);
      setHeaderEditColumnMeta(columnMeta);
      setHeaderEditOpen(true);
    },
    [allColumns]
  );

  const handleSaveSingleEdit = useCallback(
    async (itemId: string, columnKey: string, value: unknown) => {
      setAllAssemblies((prev = []) => {
        const list = Array.isArray(prev) ? [...prev] : [];
        const idx = list.findIndex((a) => !!a && a.id === itemId);
        if (idx === -1) return prev;

        const assembly = list[idx];
        const existingTemplate =
          assembly && assembly.templateFieldData && typeof assembly.templateFieldData === 'object'
            ? assembly.templateFieldData
            : {};

        const newTemplate = { ...existingTemplate, [columnKey]: value };
        const updated = { ...assembly, templateFieldData: newTemplate };

        list[idx] = updated;
        return list;
      });
      if (saveSingleEdit) {
        await saveSingleEdit(itemId, columnKey, value);
      }
    },
    [saveSingleEdit, setAllAssemblies]
  );

  const isCheckedAll = useMemo(() => {
    if (!displayedAssemblies || displayedAssemblies.length === 0) return false;
    return selectedAssemblyIds.length === displayedAssemblies.length;
  }, [displayedAssemblies, selectedAssemblyIds.length]);

  const toggleAddColumn = useCallback(() => {
    setColumnToEdit(null);
    setAddColumnOpen((s) => !s);
  }, []);

  const openColumnModal = useCallback((col: Columns) => {
    setColumnToEdit(col);
    setAddColumnOpen(true);
  }, []);

  const handleCreateColumn = useCallback((newColumn: Columns) => {
    setVisibleColumns((prev) =>
      prev.includes(newColumn.columnName) ? prev : [...prev, newColumn.columnName]
    );
  }, []);

  const CustomRow = useMemo(
    () => (props: GridCustomRowProps) => <RowRender {...props} exitEdit={exitEdit} />,
    [exitEdit]
  );
  const canEditCells = isAdmin || canManageInspectionPlans;
  const editableCellTypes = useMemo(
    () => (props: GridCustomCellProps) => (
      <CustomCell
        {...props}
        tooltipContainer={tooltipContainer}
        onSave={handleSaveSingleEdit}
        exitEdit={exitEdit}
        columns={activeColumns}
        canEdit={canEditCells}
        isEditMode={true}
      />
    ),
    [handleSaveSingleEdit, exitEdit, activeColumns, canEditCells, tooltipContainer]
  );

  return (
    <>
      <EditCellModal
        open={headerEditOpen}
        onClose={() => setHeaderEditOpen(false)}
        saveBulkEdit={async (newValue: unknown) => {
          if (saveBulkEdit && headerEditColumnKey) {
            await saveBulkEdit(headerEditColumnKey, newValue, selectedAssemblyIds);
          }
        }}
        cellData={{
          columnName: headerEditColumnName,
          columnKey: headerEditColumnKey,
          cellType: headerEditColumnMeta?.displayConfig?.type,
          options: headerEditColumnMeta?.options,
        }}
        onSuccess={() => {
          showSnackBar('Table values are edited successfully', 'success');
          getFilteredAssemblies();
        }}
        onError={() => showSnackBar('Failed to edit table values', 'error')}
      />

      <ColumnsSelectionDialog
        open={dialogOpen}
        columns={dialogColumns}
        heading="Enable the columns you would like to see in the table"
        subHeading="Select the columns you want to display:"
        applyButtonText="Apply"
        userPreferenceKey="COMPONENT"
        userPreferenceType="columns"
        onApply={handleApplyColumns}
        onClose={() => setDialogOpen(false)}
      />
      <LoadViewModal open={loadViewOpen} handleCloseView={() => setLoadViewOpen(false)} />
      <ColumnModal
        open={addColumnOpen}
        initialColumns={initialColumns}
        initialColumn={columnToEdit}
        onClose={() => toggleAddColumn()}
        onCreate={(col) => handleCreateColumn(col)}
        onUpdate={(col) => {
          setAllColumns((prev) =>
            prev.map((column) =>
              column.templateFieldKey === col.templateFieldKey
                ? {
                    ...column,
                    ...col,
                    columnName: col.columnName,
                  }
                : column
            )
          );
          const oldName = columnToEdit?.columnName;

          setVisibleColumns((prev) =>
            prev.map((name) => (name === oldName ? col.columnName : name))
          );
        }}
      />
      <div className="kendo-equipment-table-wrapper">
        <div
          className="kendo-equipment-table-controls"
          style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <InspectionPlansPills />
          </div>
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}
          >
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setAiDrawerOpen(true);
              }}
              aria-label="planner-ai"
              sx={{ textTransform: 'none', borderRadius: 1 }}
            >
              Planner AI
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                toggleAddColumn();
              }}
              aria-label="add-new-column"
              sx={{ textTransform: 'none', borderRadius: 1, px: 1.5 }}
            >
              Add New Column
            </Button>
            {(isAdmin || canManageInspectionPlans) && (
              <Tooltip title="Save view" placement="top">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setSaveViewOpen(true);
                  }}
                  aria-label="save-view"
                  sx={{ p: 1 }}
                >
                  <AbyssIcon iconName="save-view" style={{ width: '2rem', height: '2rem' }} />
                </IconButton>
              </Tooltip>
            )}
            {(isAdmin || canManageInspectionPlans) && (
              <Tooltip title="Load view" placement="top">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setLoadViewOpen(true);
                  }}
                  aria-label="load-view"
                  sx={{ p: 1 }}
                >
                  <AbyssIcon iconName="load-view" style={{ width: '2rem', height: '2rem' }} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                toggleDialog();
              }}
              sx={{ p: 1 }}
              aria-label="table-settings"
            >
              <AbyssIcon iconName="gear" style={{ width: '2rem', height: '2rem' }} />
            </IconButton>
          </div>
        </div>
        <SaveViewModal
          open={saveViewOpen}
          onClose={() => setSaveViewOpen(false)}
          selectedTemplateFilters={selectedTemplateFilters}
          selectedDeckIds={filteredDeckIds}
          selectedWorkpackIds={selectedWorkpackIds}
          selectedPaintRegionIds={selectedPaintRegionIds}
        />
        <div className="kendo-equipment-table-header-row">
          <div
            className="kendo-equipment-table-container"
            ref={setTooltipContainer}
            style={{ position: 'relative' }}
          >
            {assembliesLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255,255,255,0.85)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 11,
                  pointerEvents: 'auto',
                }}
                role="status"
                aria-live="polite"
                aria-busy={true}
              >
                <PageLoader variant="lg" flashing hideProgress />
              </Box>
            )}
            {aiLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={48} />
                <Typography sx={{ mt: 2, fontWeight: 500 }}>
                  AI is processing your request...
                </Typography>
              </Box>
            )}
            <ComponentReportsProvider
              structureId={structureId}
              assemblyIds={(displayedAssemblies || []).map((assembly) => assembly.id)}
            >
              <Grid
                data={displayedAssemblies || []}
                // assembly id
                dataItemKey="id"
                skip={page * rowsPerPage}
                take={rowsPerPage}
                total={assembliesCount}
                pageable={true}
                onPageChange={handlePageChange}
                pager={MyPager}
                sortable={false}
                resizable={true}
                className="kendo-equipment-grid"
                onEditChange={handleEditChange}
                rows={{ data: CustomRow }}
                onRowClick={(e) => handleRowClick(e.dataItem)}
                edit={edit}
                cells={{
                  edit: {
                    text: editableCellTypes,
                    boolean: editableCellTypes,
                    numeric: editableCellTypes,
                  },
                }}
                editable={{ mode: 'incell' }}
              >
                {activeColumns.map((column, index) => {
                  const dimensions = getColumnDimensions(column, activeColumns.length);
                  const isFirstColumn = index === 0;
                  const isLastColumn = index === activeColumns.length - 1;

                  const HeaderCellComponent = (props: GridCustomHeaderCellProps) => (
                    <CustomHeaderCell
                      {...props}
                      column={column}
                      columnIndex={index}
                      totalColumns={activeColumns.length}
                      isFirstColumn={isFirstColumn}
                      isLastColumn={isLastColumn}
                      onSort={handleSort}
                      onToggleColumnDialog={toggleDialog}
                      onHeaderCheckboxChange={headerCheckBoxChange}
                      onOpenHeaderEditModal={onOpenHeaderEditModal}
                      onOpenColumnModal={openColumnModal}
                      selectedRowIds={selectedAssemblyIds}
                      isChecked={isCheckedAll}
                      orderBy={sortBy}
                      order={sortDirection}
                    />
                  );

                  const CellComponent = (props: GridCustomCellProps) => {
                    return (
                      <CustomCell
                        {...props}
                        tooltipContainer={tooltipContainer}
                        column={column}
                        totalColumns={activeColumns.length}
                        isFirstColumn={isFirstColumn}
                        isSelected={props.dataItem.id === selectedAssemblyId}
                        isChecked={selectedAssemblyIds.includes(props.dataItem.id)}
                        onCheckboxChange={() => handleRowCheckBoxChange(props.dataItem.id)}
                        onCellClick={(e, item) => {
                          if (column.onCellClick) {
                            column.onCellClick(e, item);
                          }
                        }}
                        saveSingleEdit={handleSaveSingleEdit}
                        setEdit={setEdit}
                        canEdit={canEditCells}
                      />
                    );
                  };

                  const cellType = column.displayConfig?.type;
                  const isKendoIncellEditable =
                    !!column.enableInlineEdit && cellType !== 'DATE' && cellType !== 'DROPDOWN';

                  return (
                    <GridColumn
                      key={column.key}
                      field={column.key}
                      title={column.columnName}
                      width={dimensions.width}
                      locked={column.locked ?? false}
                      editable={isKendoIncellEditable || undefined}
                      cells={{
                        headerCell: HeaderCellComponent,
                        data: CellComponent,
                      }}
                    />
                  );
                })}
              </Grid>
            </ComponentReportsProvider>
          </div>
        </div>
        <KendoAIChatAssitant open={aiDrawerOpen} onClose={() => setAiDrawerOpen(false)}>
          <AIChat
            onClose={() => setAiDrawerOpen(false)}
            data={(allAssemblies || []) as Assembly[]}
            onSort={handleAISort}
            onShowAllColumns={handleAIShowAllColumns}
            onHideAllColumns={handleAIHideAllColumns}
            onHideColumn={handleAIHideColumn}
            onReset={handleAIReset}
            onUpdateAssemblies={handleAIUpdateAssemblies}
            onLoadingChange={setAiLoading}
            onAppliedFiltersChange={handleAIAppliedFiltersChange}
            selectedTemplateFilters={selectedTemplateFilters}
            pageInfo={{
              skip: page * rowsPerPage,
              limit: rowsPerPage,
              sortBy,
              sortDir: sortDirection,
            }}
          />
        </KendoAIChatAssitant>
      </div>
    </>
  );
};
